import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import './scrollBar.scss';
import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
import { useUpdateEffect } from 'ahooks';
import { systemIsWindow } from '@web-mail/util';
const noop = () => { };
const HorizontalScrollBar = props => {
    const { scrollLeft = 0, scrollWidth = 0, onScroll, show = true, tabKey, horizontalPadding = 0, onDragStart = noop, onDragEnd = noop, viewWidth, } = props;
    const [thumbPosition, setThumbPosition] = useState(0);
    const [thumbWidth, setThumbWidth] = useState(0);
    const trackRef = useRef(null);
    const thumbRef = useRef(null);
    const lock = useRef(false);
    const clickThumbX = useRef(0);
    const [thumbMaxWidth, setThumbMaxWidth] = useState(0);
    const [isDrag, setIsDrag] = useState(false);
    const [showAn, setShowAn] = useState(false);
    const [localShowRes, setLocalShowRes] = useState(show);
    const onScrollRef = useCreateCallbackForEvent(onScroll);
    const onDragStartRef = useCreateCallbackForEvent(onDragStart);
    const onDragEndRef = useCreateCallbackForEvent(onDragEnd);
    const inWindows = useMemo(() => {
        return systemIsWindow();
    }, []);
    const localShow = useMemo(() => {
        return thumbWidth < thumbMaxWidth && thumbWidth != 0;
    }, [thumbWidth, thumbMaxWidth]);
    const getViewWidth = useCreateCallbackForEvent(() => {
        if (trackRef.current) {
            const trackRect = trackRef.current.getBoundingClientRect();
            return viewWidth != null ? viewWidth : trackRect.width;
        }
        return viewWidth;
    });
    const handleScroll = useCallback(() => {
        var _a, _b;
        if (trackRef.current && thumbRef.current && ((_a = trackRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect) && ((_b = thumbRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect)) {
            const trackRect = trackRef.current.getBoundingClientRect();
            const thumbRect = thumbRef.current.getBoundingClientRect();
            const thumbMax = trackRect.width - thumbRect.width;
            const scrollPos = scrollLeft || 0;
            const thumbPos = Math.max(0, Math.min((scrollPos / scrollWidth) * trackRect.width, thumbMax));
            setThumbPosition(thumbPos);
        }
    }, [scrollLeft, scrollWidth]);
    const handleScrollRef = useCreateCallbackForEvent(handleScroll);
    const handleDragMove = useCallback(event => {
        var _a, _b;
        if (trackRef.current && thumbRef.current && ((_a = trackRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect) && ((_b = thumbRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect)) {
            const trackRect = trackRef.current.getBoundingClientRect();
            const thumbRect = thumbRef.current.getBoundingClientRect();
            const thumbMax = trackRect.width - thumbRect.width;
            const thumbX = event.clientX - trackRect.left - clickThumbX.current;
            const thumbPos = Math.max(0, Math.min(thumbX, thumbMax));
            const scrollPos = (thumbPos / trackRect.width) * scrollWidth;
            setThumbPosition(thumbPos);
            onScrollRef(scrollPos);
        }
    }, [scrollWidth]);
    const handleDragMoveRef = useCreateCallbackForEvent(handleDragMove);
    const handleDragEnd = useCallback(() => {
        lock.current = false;
        clickThumbX.current = 0;
        onDragEndRef();
        setIsDrag(false);
        document.removeEventListener('mousemove', handleDragMoveRef);
        document.removeEventListener('mouseup', handleDragEndRef);
    }, []);
    const handleDragEndRef = useCreateCallbackForEvent(handleDragEnd);
    const handleDragStart = useCallback(event => {
        event.preventDefault();
        event.stopPropagation();
        lock.current = true;
        onDragStartRef();
        setIsDrag(true);
        if (thumbRef.current && thumbRef.current.getBoundingClientRect) {
            const thumbRect = thumbRef.current.getBoundingClientRect();
            clickThumbX.current = event.clientX - thumbRect.left;
        }
        document.addEventListener('mousemove', handleDragMoveRef);
        document.addEventListener('mouseup', handleDragEndRef);
    }, []);
    const updateBarPosition = () => {
        if (trackRef.current && !lock.current) {
            const curThumbMaxWidth = trackRef.current.clientWidth;
            let thumbWidth = Math.max(0, (getViewWidth() / scrollWidth) * curThumbMaxWidth);
            if (thumbWidth > curThumbMaxWidth) {
                thumbWidth = curThumbMaxWidth;
            }
            setThumbWidth(thumbWidth);
            setThumbMaxWidth(curThumbMaxWidth);
            setTimeout(() => {
                handleScrollRef();
            }, 0);
        }
    };
    const updateBarPositionRef = useCreateCallbackForEvent(updateBarPosition);
    const handleTrackClick = (event) => {
        if (event && trackRef.current && thumbRef.current) {
            event.stopPropagation();
            event.preventDefault();
            const clickX = event.clientX - trackRef.current.getBoundingClientRect().left;
            const trackRect = trackRef.current.getBoundingClientRect();
            const thumbRect = thumbRef.current.getBoundingClientRect();
            const thumbX = clickX - thumbWidth / 2;
            const thumbMax = trackRect.width - thumbRect.width;
            const thumbPos = Math.max(0, Math.min(thumbX, thumbMax));
            const scrollPos = (thumbPos / trackRect.width) * scrollWidth;
            onScrollRef(scrollPos);
            setShowAn(true);
            setThumbPosition(thumbX);
            setTimeout(() => {
                setShowAn(false);
            }, 0);
        }
    };
    useEffect(() => {
        updateBarPositionRef();
    }, [scrollLeft, scrollWidth]);
    useEffect(() => {
        const observer = new ResizeObserver(() => {
            updateBarPositionRef();
        });
        const currentTrack = trackRef.current;
        if (currentTrack) {
            observer.observe(currentTrack);
        }
        return () => {
            observer.disconnect();
        };
    }, []);
    useUpdateEffect(() => {
        if (tabKey) {
            setShowAn(true);
            setTimeout(() => {
                setShowAn(false);
            }, 500);
        }
    }, [tabKey]);
    useEffect(() => {
        updateBarPositionRef();
        setLocalShowRes(show);
    }, [show]);
    return (React.createElement("div", { className: ` vscrollbar-readmail horizontal ${localShowRes && localShow ? 'show' : 'hidden'} ${inWindows ? 'vst_inWindow' : ''}`, style: { margin: `0px ${horizontalPadding}px`, opacity: isDrag ? 1 : undefined } },
        React.createElement("div", { className: "track", ref: trackRef, onClick: handleTrackClick },
            React.createElement("div", { className: `thumb ${showAn ? 'scrollAn' : ''}`, ref: thumbRef, style: { left: `${thumbPosition}px`, width: `${thumbWidth}px` }, onMouseDown: handleDragStart }))));
};
export default HorizontalScrollBar;
